import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Percent, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { money, num, pct } from '../../utils/nurseryMoney';
import { Dialog, ReasonDialog, SeatRequired } from '../../components/nursery/accounts/AccountBits';
import { RebateAgreementDialog } from '../../components/nursery/accounts/RebateAgreementDialog';
import {
  REBATE_KIND_LABELS,
  REBATE_SETTLEMENT_LABELS,
  type RebateAccrual,
  type RebateAgreement,
  type RebateStatementSummary,
} from '../../types/nurseryAccounts';

/**
 * /accounts/rebates — what each paying customer gets back on its spend.
 *
 * A rebate is not a price: the price list says what a store is charged, the
 * agreement says what its head office gets back. Agreements are ended and
 * replaced, never edited, so an old settlement can always be reconstructed.
 * Where an agreement stands this period is worked out by the server on every
 * read; nothing here does that arithmetic.
 */

/** What is loaded beside each agreement. */
interface Extras {
  accrual: RebateAccrual | null;
  /** The period before the current one, when it has ended and is not yet closed. */
  toClose: RebateAccrual | null;
  statements: RebateStatementSummary[];
  error: string | null;
}

const day = (d: string | null | undefined): string => String(d ?? '').slice(0, 10);

const PERIOD_WORDS = { month: 'each month', quarter: 'each quarter', year: 'each year' } as const;

function Rebates() {
  const tz = useOrgTimezone();
  const [params] = useSearchParams();
  const customerId = Number(params.get('customer_id')) || 0;
  const { hasAccess: canRead, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');

  const [agreements, setAgreements] = useState<RebateAgreement[]>([]);
  const [extras, setExtras] = useState<Record<number, Extras>>({});
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showSites, setShowSites] = useState<Record<number, boolean>>({});
  const [creating, setCreating] = useState(false);
  const [ending, setEnding] = useState<RebateAgreement | null>(null);
  const [closing, setClosing] = useState<{ agreement: RebateAgreement; accrual: RebateAccrual } | null>(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const fmt = useCallback((d: string | null | undefined) => (day(d) ? formatLocalDateInTz(day(d), tz) : ''), [tz]);

  /** "June 2026" for a month, "Jul 2025 to Jun 2026" for anything longer. */
  const periodName = useCallback((a: RebateAgreement, p: { start: string; end: string }) => {
    if (a.period === 'month') return formatLocalDateInTz(day(p.start), tz, { month: 'long', year: 'numeric' });
    const short: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
    return `${formatLocalDateInTz(day(p.start), tz, short)} to ${formatLocalDateInTz(day(p.end), tz, short)}`;
  }, [tz]);

  const loadExtras = useCallback(async (a: RebateAgreement): Promise<Extras> => {
    const out: Extras = { accrual: null, toClose: null, statements: [], error: null };
    try {
      const detail = await nurseryAccountsService.getRebateAgreement(a.id);
      if (detail.success && detail.data) out.statements = detail.data.statements || [];
      // An ended agreement, and a discount taken at payment, have no period running.
      if (a.revoked_at || a.kind === 'settlement_discount') return out;
      const now = await nurseryAccountsService.getRebateAccrual(a.id);
      if (!now.success || !now.data) {
        out.error = errorMessage(now, 'Could not work out where this agreement stands');
        return out;
      }
      out.accrual = now.data;
      // Whether the period before this one still needs closing. Not asked
      // about a period that ended before the agreement began.
      const before = addDays(day(now.data.period.start), -1);
      if (before >= day(a.effective_from)) {
        const prev = await nurseryAccountsService.getRebateAccrual(a.id, before);
        if (prev.success && prev.data?.closable) out.toClose = prev.data;
      }
    } catch (e) {
      out.error = errorMessage(e, 'Could not work out where this agreement stands');
    }
    return out;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.getRebateAgreements({
        customer_id: customerId || undefined,
        include_revoked: includeRevoked || undefined,
      });
      if (!res.success) {
        setError(errorMessage(res, 'Could not load rebate agreements'));
        return;
      }
      const list = res.data || [];
      setAgreements(list);
      const loaded = await Promise.all(list.map(loadExtras));
      setExtras(Object.fromEntries(list.map((a, i) => [a.id, loaded[i]])));
    } catch (e) {
      setError(errorMessage(e, 'Could not load rebate agreements'));
    } finally {
      setLoading(false);
    }
  }, [customerId, includeRevoked, loadExtras]);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  // The name for the "whose agreements" line, which must show even when the
  // customer has no agreement yet.
  useEffect(() => {
    setCustomerName(null);
    if (!canRead || !customerId) return;
    let live = true;
    nurseryService.getCustomer(customerId)
      .then((res) => { if (live && res.success && res.data) setCustomerName(res.data.name); })
      .catch(() => { /* the agreements still name the customer */ });
    return () => { live = false; };
  }, [canRead, customerId]);

  const confirmClose = async () => {
    if (!closing) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      const res = await nurseryAccountsService.closeRebatePeriod(closing.agreement.id, day(closing.accrual.period.start));
      if (!res.success) {
        setCloseError(errorMessage(res, 'Could not close the period'));
        return;
      }
      const name = periodName(closing.agreement, closing.accrual.period);
      const note = res.data?.credit_note;
      setNotice(note
        ? `${name} is closed for ${closing.agreement.customer_name}. Credit note ${note.credit_note_number} was raised for ${money(note.total_amount)} including GST.`
        : `${name} is closed for ${closing.agreement.customer_name}. No credit note was raised.`);
      setClosing(null);
      await load();
    } catch (e) {
      setCloseError(errorMessage(e, 'Could not close the period'));
    } finally {
      setCloseBusy(false);
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canRead) return <SeatRequired seat="user" what="The rebates page" />;

  // Grouped by customer, in the order the server sent them (by name).
  const groups: { id: number; name: string; code: string | null; items: RebateAgreement[] }[] = [];
  for (const a of agreements) {
    let g = groups.find((x) => x.id === a.customer_id);
    if (!g) {
      g = { id: a.customer_id, name: a.customer_name, code: a.customer_code, items: [] };
      groups.push(g);
    }
    g.items.push(a);
  }
  const filterName = customerName ?? agreements[0]?.customer_name ?? null;

  const partsOf = (a: RebateAgreement) => {
    if (a.kind === 'volume_tiered') {
      const bands = [...a.bands].sort((x, y) => num(x.threshold) - num(y.threshold));
      return (
        <>
          <ul className="text-sm text-neutral-800 space-y-0.5">
            {bands.map((b, i) => (
              <li key={b.id ?? i}>From {money(b.threshold)}: <span className="font-medium">{pct(b.rate_pct)}</span></li>
            ))}
          </ul>
          <p className="text-xs text-neutral-500 break-words">
            {a.band_mode === 'marginal'
              ? 'Each band applies only to the spend above it (marginal).'
              : 'The band reached applies to all spend in the period (retrospective).'}
          </p>
        </>
      );
    }
    if (a.kind === 'settlement_discount') {
      const rate = a.components[0]?.rate_pct ?? a.total_rate_pct;
      return (
        <p className="text-sm text-neutral-800 break-words">
          <span className="font-medium">{pct(rate)}</span> off an invoice
          {a.discount_days ? ` paid within ${a.discount_days} day${a.discount_days === 1 ? '' : 's'}` : ' paid early'}.
        </p>
      );
    }
    const fixed = a.kind === 'fixed_amount';
    return (
      <>
        <ul className="text-sm text-neutral-800 space-y-0.5">
          {a.components.map((c, i) => (
            <li key={c.id ?? i} className="break-words">
              {c.label}:{' '}
              <span className="font-medium">
                {fixed ? `${money(c.fixed_amount)} ${PERIOD_WORDS[a.period]}` : pct(c.rate_pct)}
              </span>
              {c.account_code ? <span className="text-xs text-neutral-500"> · account {c.account_code}</span> : null}
            </li>
          ))}
        </ul>
        {!fixed && a.components.length > 1 && (
          <p className="text-sm font-medium text-neutral-900">{pct(a.total_rate_pct)} in all</p>
        )}
      </>
    );
  };

  const figure = (label: string, value: string, strong?: boolean) => (
    <div className="min-w-0">
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className={cn('text-sm break-words', strong ? 'font-semibold text-neutral-900' : 'text-neutral-800')}>{value}</dd>
    </div>
  );

  const accrualOf = (a: RebateAgreement, acc: RebateAccrual) => (
    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium text-neutral-900 break-words">
        This period: {fmt(acc.period.start)} to {fmt(acc.period.end)}
      </p>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        {figure(`Spend (${acc.invoices} invoice${acc.invoices === 1 ? '' : 's'})`, money(acc.spend))}
        {figure('Earned', money(acc.earned))}
        {figure('Credited so far', money(acc.already_credited))}
        {figure(num(acc.balance) < 0 ? 'Credited over what was earned' : 'Still owed', money(Math.abs(num(acc.balance))), true)}
      </dl>
      {acc.band && (
        <p className="text-sm text-neutral-700 break-words">
          Band reached: from {money(acc.band.threshold)}, {pct(acc.band.rate_pct)}.
        </p>
      )}
      {acc.next_band && (
        <p className="text-sm text-neutral-700 break-words">
          Another {money(acc.next_band.spend_to_reach)} to reach {pct(acc.next_band.rate_pct)}.
        </p>
      )}
      {acc.running && (
        <p className="text-sm text-neutral-600 break-words">
          At the rate so far, the period ends on about {money(acc.projected_spend)} of spend, earning {money(acc.projected_earned)}.
        </p>
      )}
      {acc.by_site.length > 0 && (
        <div>
          <button type="button" aria-expanded={!!showSites[a.id]}
            onClick={() => setShowSites((s) => ({ ...s, [a.id]: !s[a.id] }))}
            className="text-sm text-primary-700 hover:underline">
            {showSites[a.id] ? 'Hide spend by site' : 'Show spend by site'}
          </button>
          {showSites[a.id] && (
            <ul className="mt-2 divide-y divide-neutral-200 border-t border-neutral-200">
              {acc.by_site.map((s) => (
                <li key={s.site_id} className="py-1.5 flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                  <span className="min-w-0 break-words text-neutral-800">
                    {s.site_name}{s.customer_code ? ` #${s.customer_code}` : ''}
                    <span className="text-xs text-neutral-500"> · {s.invoices} invoice{s.invoices === 1 ? '' : 's'}</span>
                  </span>
                  <span className="font-medium text-neutral-900">{money(s.spend)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  const statementsOf = (list: RebateStatementSummary[]) => (
    <div>
      <p className="text-sm font-medium text-neutral-900 mb-1">Closed periods</p>
      <ul className="space-y-2">
        {list.map((s) => (
          <li key={s.id} className="border border-neutral-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-neutral-800 break-words">{fmt(s.period_start)} to {fmt(s.period_end)}</p>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
              {figure('Spend', money(s.spend))}
              {figure('Earned', money(s.earned))}
              {figure('Already credited', money(s.already_credited))}
              {figure('Balance', money(s.balance), true)}
            </dl>
            <p className="text-xs text-neutral-500 break-words">
              {s.credit_note_number ? `Credit note ${s.credit_note_number}` : 'No credit note raised'}
              {' · '}fingerprint <span className="font-mono break-all">{String(s.content_hash || '').slice(0, 12)}</span>
            </p>
          </li>
        ))}
      </ul>
    </div>
  );

  const cardOf = (a: RebateAgreement) => {
    const x = extras[a.id];
    const ended = !!a.revoked_at;
    return (
      <li key={a.id} className={cn('border rounded-xl p-4 space-y-3',
        ended ? 'bg-neutral-50 border-neutral-200 opacity-80' : 'bg-white border-neutral-200')}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className={cn('font-semibold break-words', ended ? 'text-neutral-600' : 'text-neutral-900')}>{a.name}</h3>
            <p className="text-sm text-neutral-600 break-words">
              {REBATE_KIND_LABELS[a.kind]}
              {a.kind !== 'settlement_discount' && <> · {REBATE_SETTLEMENT_LABELS[a.settlement]}</>}
            </p>
          </div>
          {ended && (
            <span className="inline-flex items-center rounded-full bg-neutral-200 text-neutral-700 px-2 py-0.5 text-xs font-semibold">
              Ended {formatDateInTz(a.revoked_at as string, tz)}
            </span>
          )}
        </div>

        {ended && a.revoke_reason && (
          <p className="text-sm text-neutral-600 break-words">Why it ended: {a.revoke_reason}</p>
        )}

        <div className="space-y-1">{partsOf(a)}</div>

        <p className="text-xs text-neutral-500 break-words">
          {a.kind !== 'settlement_discount' && (
            <>Worked out {PERIOD_WORDS[a.period]}, the year starting in{' '}
              {formatLocalDateInTz(`2026-${String(a.period_start_month).padStart(2, '0')}-01`, tz, { month: 'long' })}.{' '}</>
          )}
          In force from {fmt(a.effective_from)}{a.effective_to ? ` to ${fmt(a.effective_to)}` : ', with no end date'}.
          {a.include_delivery ? ' Delivery charges count.' : ''}
        </p>

        {a.notes && <p className="text-sm text-neutral-600 break-words whitespace-pre-line">{a.notes}</p>}

        {x?.error && <p className="text-sm text-red-700 break-words">{x.error}</p>}
        {x?.accrual && accrualOf(a, x.accrual)}

        {x?.toClose && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-amber-900 break-words">
              {periodName(a, x.toClose.period)} has ended and has not been closed.
              {num(x.toClose.balance) > 0 ? ` ${money(x.toClose.balance)} is still owed for it.` : ''}
              {!isAdmin && ' An Accounts Admin can close it.'}
            </p>
            {isAdmin && (
              <Button size="sm" onClick={() => { setCloseError(null); setClosing({ agreement: a, accrual: x.toClose as RebateAccrual }); }}>
                Close {periodName(a, x.toClose.period)}
              </Button>
            )}
          </div>
        )}

        {x && x.statements.length > 0 && statementsOf(x.statements)}

        {isAdmin && !ended && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setEnding(a)}>End agreement</Button>
          </div>
        )}
      </li>
    );
  };

  const closeBalance = closing ? num(closing.accrual.balance) : 0;

  return (
    <>
      <PageHeader
        title="Rebates"
        subtitle="What each paying customer gets back on its spend."
        icon={<Percent className="h-6 w-6 text-white" />}
        actionButtons={isAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New agreement
            </Button>
          </div>
        ) : undefined}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="text-sm text-neutral-600 space-y-2 break-words">
          <p>
            A rebate is what a customer gets back on its spend. It is not a price: the price list says what a store is
            charged, and the agreement says what whoever pays the account gets back. An agreement is ended and replaced,
            never changed, so an old period can always be worked out again.
          </p>
          <p>
            For a rebate the customer takes off its payments, the usual setting is “{REBATE_SETTLEMENT_LABELS.per_invoice.toLowerCase()}”.
            A credit note is raised with every invoice, so the amount owed on the invoice is exactly what the customer
            pays, and nothing needs uploading.
          </p>
          <p>Every amount on this page is before GST.</p>
        </div>

        {customerId > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 break-words">
            Showing agreements with{' '}
            <Link to={`/accounts/customers/${customerId}`} className="font-medium underline">
              {filterName ?? 'this customer'}
            </Link>{' '}only.{' '}
            <Link to="/accounts/rebates" className="underline">Show every customer</Link>
          </div>
        )}

        <label htmlFor="rebates-ended" className="flex items-center gap-2 text-sm text-neutral-700">
          <input id="rebates-ended" type="checkbox" checked={includeRevoked} onChange={(e) => setIncludeRevoked(e.target.checked)} />
          Show ended agreements
        </label>

        {notice && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{notice}</div>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : groups.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500 break-words">
            {customerId ? 'This customer has no rebate agreement.' : 'No rebate agreements yet.'}
            {isAdmin ? ' Use New agreement to add one.' : ' An Accounts Admin can add one.'}
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.id} className="space-y-2">
              <h2 className="text-base font-semibold text-neutral-900 break-words">
                <Link to={`/accounts/customers/${g.id}`} className="hover:underline">{g.name}</Link>
                {g.code ? <span className="text-sm font-normal text-neutral-500"> · {g.code}</span> : null}
              </h2>
              <ul className="space-y-3">{g.items.map(cardOf)}</ul>
            </section>
          ))
        )}
      </div>

      {creating && (
        <RebateAgreementDialog
          initialCustomerId={customerId || undefined}
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            setNotice(`${a.name} is saved for ${a.customer_name}.`);
            load();
          }}
        />
      )}

      {ending && (
        <ReasonDialog
          title={`End ${ending.name}`}
          body={
            <>
              Agreements are ended and replaced, never edited. Ending this one stops it from today for{' '}
              {ending.customer_name}. Invoices already credited keep their credit. To change the terms, end it and add
              a new agreement.
            </>
          }
          label="Why is it ending?"
          confirmLabel="End agreement"
          danger
          onClose={() => setEnding(null)}
          onConfirm={async (reason) => {
            const res = await nurseryAccountsService.revokeRebateAgreement(ending.id, reason);
            if (!res.success) throw new Error(errorMessage(res, 'Could not end the agreement'));
            setNotice(`${ending.name} has ended.`);
            setEnding(null);
            await load();
          }}
        />
      )}

      {closing && (
        <Dialog
          title={`Close ${periodName(closing.agreement, closing.accrual.period)}`}
          subtitle={`${closing.agreement.name} · ${closing.agreement.customer_name}`}
          onClose={() => { if (!closeBusy) setClosing(null); }}
          footer={
            <>
              <Button variant="outline" onClick={() => setClosing(null)} disabled={closeBusy}>Cancel</Button>
              <Button onClick={confirmClose} disabled={closeBusy}>{closeBusy ? 'Closing…' : 'Close the period'}</Button>
            </>
          }
        >
          <p className="text-sm text-neutral-700 break-words">
            {fmt(closing.accrual.period.start)} to {fmt(closing.accrual.period.end)}. Amounts are before GST.
          </p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
            {figure('Spend', money(closing.accrual.spend))}
            {figure('Earned', money(closing.accrual.earned))}
            {figure('Already credited', money(closing.accrual.already_credited))}
            {figure('Balance', money(closing.accrual.balance), true)}
          </dl>
          <div className="text-sm text-neutral-700 space-y-2 break-words">
            <p>The statement for this period is frozen and cannot be changed afterwards.</p>
            {closing.agreement.settlement === 'billed_to_us' ? (
              <p>No credit note is raised, because {closing.agreement.customer_name} bills you for this rebate. The statement records what was earned.</p>
            ) : closeBalance > 0 ? (
              <p>A credit note for {money(closeBalance)} plus GST is raised on {closing.agreement.customer_name}’s account.</p>
            ) : closeBalance < 0 ? (
              <p>
                More was credited during the period than it earned. Nothing is raised; the {money(Math.abs(closeBalance))}{' '}
                difference is recorded on the statement and left for a person to follow up.
              </p>
            ) : (
              <p>Nothing is owed for this period, so no credit note is raised.</p>
            )}
          </div>
          {closeError && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{closeError}</div>
          )}
        </Dialog>
      )}
    </>
  );
}

export default Rebates;
